import { api, type Level, type Metric, type RankResult } from "./api.ts";
import { fromOf, inPurchaseRateState, taxRuleNote, usesPurchaseRule } from "../shared/regions.ts";
import { fmt, LEVEL_LABEL } from "../shared/format.ts";
import { el, nodes } from "./dom.ts";
import { pager, sortHeader } from "./widgets.ts";

export interface TableDeps {
  metrics: Map<string, Metric>;
  getLevel(): Level;
  getMetric(): Metric;
  /** the area the table is filtered to (a state, metro or county), or null for the whole country */
  /** the area the table is limited to; `scope`: the view's own (the deep state), which "show all" can't widen */
  getWithin(): { geo_id: string; name: string; scope?: true } | null;
  clearWithin(): void;
  onRowClick(geoId: string): void;
  onClose(): void;
}

const DEFAULT_COLS = ["home_value", "home_value_yoy", "inventory_yoy", "price_cut_pct", "forecast_pct"];
const PAGE = 100;

/** Sortable ranking of every area at the current level, with CSV export. */
export class TableView {
  private sort: "asc" | "desc" = "desc";
  /** Sort by the map's metric in this direction (a link such as Ask's "cheapest …" table). */
  setSort(metric: string, dir: "asc" | "desc") {
    this.lastMetric = metric;
    this.sortBy = undefined;
    this.sort = dir;
  }
  private sortBy?: string;
  private offset = 0;
  private cols: string[] = DEFAULT_COLS;
  private seq = 0; // newest render wins
  private lastMetric?: string;

  constructor(private readonly root: HTMLElement, private readonly d: TableDeps) {}

  get open() {
    return !this.root.hidden;
  }

  show() {
    this.root.hidden = false;
    this.offset = 0;
    void this.render();
  }

  hide() {
    this.root.hidden = true;
  }

  refresh() {
    if (this.open) {
      this.offset = 0;
      void this.render();
    }
  }

  private async render() {
    const seq = ++this.seq;
    const level = this.d.getLevel() === "us" ? "state" : this.d.getLevel();
    const m = this.d.getMetric();
    // picking a new metric on the map sorts the table by it again
    if (m.id !== this.lastMetric) { this.lastMetric = m.id; this.sortBy = undefined; this.sort = "desc"; }
    const has = (id: string) => !!this.d.metrics.get(id)?.available[level];
    // a sort column that has no data at this level (e.g. after switching level) falls back to the map's metric
    const sortBy = this.sortBy && has(this.sortBy) ? this.sortBy : m.id;
    const cols = [m.id, ...this.cols.filter((c) => c !== m.id && has(c))];
    const within = this.d.getWithin();
    this.root.replaceChildren(el("div", { class: "panel-loading" }, "Loading table…"));
    let res: RankResult;
    try {
      res = await api.rank(level, sortBy, { within: within?.geo_id ?? null, sort: this.sort, cols: cols.filter((c) => c !== sortBy), limit: PAGE, offset: this.offset });
    } catch (e) {
      if (seq !== this.seq) return;
      this.root.replaceChildren(el("div", { class: "panel-error" }, (e as Error).message));
      return;
    }
    if (seq !== this.seq) return;
    const shown = [sortBy, ...cols.filter((c) => c !== sortBy)];
    const head = el("tr", {},
      el("th", { class: "num" }, "#"),
      el("th", {}, LEVEL_LABEL[level]!),
      ...shown.map((c) => {
        const active = c === sortBy;
        return sortHeader(this.d.metrics.get(c)!.label, { active, dir: this.sort, onSort: () => {
          if (active) this.sort = this.sort === "asc" ? "desc" : "asc";
          else { this.sortBy = c; this.sort = "desc"; }
          this.offset = 0;
          void this.render();
        } });
      }));
    const body = el("tbody", {}, ...res.rows.map((r) =>
      el("tr", { tabindex: 0, onclick: () => this.d.onRowClick(r.geo_id), onkeydown: (e: Event) => { if ((e as KeyboardEvent).key === "Enter") this.d.onRowClick(r.geo_id); } },
        el("td", { class: "num muted" }, String(r.rank)),
        el("td", {}, level === "zip" ? `${r.geo_id.slice(4)} · ${r.name}` : r.name),
        ...shown.map((c) => el("td", { class: "num" }, fmt(this.d.metrics.get(c)!, r.values[c] ?? null))))));

    const pages = Math.ceil(res.total / PAGE);
    const page = Math.floor(this.offset / PAGE) + 1;
    this.root.replaceChildren(...nodes(
      el("header", { class: "table-head" },
        el("div", {},
          el("h2", {}, `${LEVEL_LABEL[level]} ranking`),
          el("div", { class: "muted small" }, `${res.total.toLocaleString()} areas${within ? ` in ${within.name}` : " nationwide"} · latest values`,
            within && !within.scope ? el("button", { class: "link", onclick: () => this.d.clearWithin() }, " (show all)") : null),
          // a payment compared across states mixes two property-tax bases: say so (shared/regions.ts)
          shown.some((c) => usesPurchaseRule(c, fromOf((id) => this.d.metrics.get(id)))) && level !== "city" && inPurchaseRateState(within ? [within.geo_id] : []) === null
            ? el("div", { class: "muted small tax-note" }, taxRuleNote(null)) : null),
        el("div", { class: "panel-actions" },
          // the server sends only exportable columns, and none for a raw Zillow / Realtor.com / Redfin series (their terms)
          this.d.metrics.get(sortBy)?.export === false
            ? el("span", { class: "muted small", title: "This data point's source doesn't allow downloads" }, "No CSV for this source")
            : el("a", { class: "btn small", href: api.rankCsvUrl(level, sortBy, within?.geo_id ?? null, shown.filter((c) => c !== sortBy)), download: "" }, "Export CSV"),
          el("button", { class: "btn icon", "aria-label": "Close table", onclick: () => this.d.onClose() }, "✕"))),
      el("div", { class: "table-scroll" }, el("table", { class: "rank" }, el("thead", {}, head), body)),
      pager(page, pages, (step) => { this.offset += step * PAGE; void this.render(); }),
    ));
  }
}
